/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {memo, useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {getLoggedUserId, selectIsHost} from '~/common/fabric/User/reducers/user';
import {WhatToBringOverlay} from '~/ViewInvitation/common/WhatToBringOverlay';
import {fetchWhatToBring, postWhatToBringSignup} from '~/ViewInvitation/reducers/apiActions';
import {
  selectEventId,
  selectEventIsFuneral,
  selectWidgets,
} from '~/ViewInvitation/selectors/eventDetails';
import {selectHasRsvpd} from '~/ViewInvitation/selectors/rsvp';
import {selectUserName} from '~/ViewInvitation/selectors/user';
import {
  selectUserHasWTBSignUp,
  selectWhatToBringItems,
  selectWhatToBringListId,
} from '~/ViewInvitation/selectors/whatToBring';
import type {ModuleProps} from '~/ViewInvitation/types/module';
import {isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {LockLineIcon} from '~library/svg/LockLineIcon';

import './WhatToBring.sass';

export interface WhatToBringModuleProps extends ModuleProps {
  restored?: boolean;
  onPropagateRestore?: (patchedEventData: any) => void;
}

export const WhatToBringModule = memo(
  ({
    restored,
    onPropagateRestore,
    readonly = true,
    store,
    editBtnRef,
    preview = isPreview(),
  }: WhatToBringModuleProps) => {
    const dispatch = useDispatch();
    const userId = useSelector(getLoggedUserId);
    const isHost = useSelector(selectIsHost);
    const name = useSelector(selectUserName);
    const eventId = useSelector(selectEventId);
    const widgets = useSelector(selectWidgets, shallowEqual);
    const items = useSelector(selectWhatToBringItems, shallowEqual) || [];
    const wtbListId = useSelector(selectWhatToBringListId);
    const userHasWTBSignUp = useSelector(selectUserHasWTBSignUp);
    const hasRSVPd = useSelector(selectHasRsvpd);
    const isFuneral = useSelector(selectEventIsFuneral);

    const [overlayOpen, setOverlayOpen] = useState(false);

    const ctaWording = () => {
      if (isHost && !readonly) return 'View list';
      if (userHasWTBSignUp) return 'View your sign up';
      return 'Sign up';
    };

    useEffect(() => {
      let eId: string | undefined;
      if (store) {
        eId = store?.getState()?.eviteEvent?.id;
      } else {
        eId = (window as any).event_id || (window as any).evite_event.id;
      }
      if (!eId) return;
      (dispatch as any)(fetchWhatToBring(eId));
    }, [store, dispatch]);

    const onOpenWhatToBring = useCallback(() => {
      setOverlayOpen(true);
    }, []);

    const onAssign = useCallback(
      async (userId: string, guestName: string, itemId: string, quantity: number) => {
        const item = items.find((i: any) => i.id === itemId);
        if (!item) {
          throw new Error('An error occurred during assignments.');
        }
        return new Promise<void>((resolve, reject) => {
          (dispatch as any)(
            postWhatToBringSignup(eventId, wtbListId, itemId, quantity, {
              userId,
              userName: guestName,
            })
          )
            .then(() => {
              resolve();
            })
            .catch((error: any) => {
              reject(error);
            });
        });
      },
      [items, dispatch, eventId, wtbListId]
    );

    const onUnassign = useCallback(
      async (userId: string, guestName: string, itemId: string) => {
        const item = items.find((i: any) => i.id === itemId);
        if (!item) return;
        const alreadyAssigned = item.selections.map((s: any) => ({
          ...s.user,
          quantity: s.quantity,
        }));
        const user = alreadyAssigned.find((a: any) => a.id === userId);
        if (!user) return;
        const quantity = 0;
        return new Promise<void>((resolve, reject) => {
          (dispatch as any)(
            postWhatToBringSignup(eventId, wtbListId, itemId, quantity, {
              userId,
              userName: guestName,
            })
          )
            .then(() => {
              resolve();
            })
            .catch((error: any) => {
              reject(error);
            });
        });
      },
      [items, dispatch, eventId, wtbListId]
    );

    useEffect(() => {
      if (restored && preview && isHost) {
        const patchedEventData = {
          widgets,
        };
        onPropagateRestore?.(patchedEventData);
      }
    }, [restored, preview, isHost, widgets, onPropagateRestore]);

    const editable = preview && !readonly;
    return (
      <>
        <EditButton data-qa-id="edit-wtb" ref={editBtnRef} hide={!editable} />
        <div className="view-invitation__what-to-bring">
          <Typography variant="label2" color="pebble" data-qa-id="what-to-bring-module-label">
            What to bring
          </Typography>
          <Typography variant="header6" data-qa-id="what-to-bring-module-title" semibold>
            Contribute to {isFuneral ? 'this event' : 'the party'}
          </Typography>
          <Typography variant="paragraph3">
            Help your host out{isFuneral ? '.' : '!'} View their What to Bring list and sign up for
            your choice now.
          </Typography>
          <div className="view-invitation__what-to-bring__footer">
            <Button
              data-qa-id={hasRSVPd ? 'view-their-list' : 'rsvp-to-contribute'}
              variant={userHasWTBSignUp && !isHost ? 'secondary' : 'primary'}
              size="small"
              onClick={onOpenWhatToBring}
              noTextWrap
              fullWidth
              disabled={preview || !hasRSVPd}
            >
              {ctaWording()}
            </Button>
            {!hasRSVPd && !isHost && (
              <Typography
                variant="paragraph2"
                className="view-invitation__what-to-bring__footer-message"
                semibold
              >
                <LockLineIcon ratio={0.75} /> RSVP to sign up
              </Typography>
            )}
          </div>
        </div>
        {!preview &&
          createPortal(
            <WhatToBringOverlay
              open={overlayOpen}
              onClose={() => setOverlayOpen(false)}
              items={items}
              userId={userId}
              name={name}
              isHost={isHost}
              hasRsvpd={hasRSVPd}
              onAssign={onAssign}
              onUnassign={onUnassign}
            />,
            document.body
          )}
      </>
    );
  }
);
